import React, { useEffect, useMemo, useState } from "react";
import "./news.scss";
import { archiveNews, getLeadFilterOptions, getNews, restoreNews, syncMyBinaProjects } from "../../api/crawlerApi";
import { extractList } from "../../api/client";
import ConsolidatedSources from "../../components/ConsolidatedSources/ConsolidatedSources";
import PaginationControls from "../../components/PaginationControls/PaginationControls";
import { getLeadStatusClass, getLeadStatusLabel } from "../../utils/statuses";
import { formatProjectValue, truncateText } from "../../utils/text";

const getScoreClass = (score) => {
  if (score >= 80) return "score score--high";
  if (score >= 60) return "score score--medium";
  return "score score--low";
};

const getExternalSourceUrl = (row) =>
  row.source_records?.find(
    (record) => ["rss", "search"].includes(record.source_type) && record.source_url
  )?.source_url || null;

const defaultStatusOptions = [
  { value: "new", label: "New" },
  { value: "synced", label: "Synced" },
  { value: "archived", label: "Archived" },
];

export default function NewsPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [filters, setFilters] = useState({ source: "", location: "", ai_score_min: "", ai_score_max: "", project_value_min: "", project_value_max: "" });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState(defaultStatusOptions);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncingMyBina, setSyncingMyBina] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [syncError, setSyncError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const loadRows = async (status = statusFilter, activeFilters = appliedFilters, targetPage = page, size = pageSize) => {
    setLoading(true);
    try {
      const response = await getNews({ status, ...activeFilters, page: targetPage, size });
      setRows(extractList(response));
      setPagination(response || { current_page: targetPage, last_page: 1, total: 0 });
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load news.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows(statusFilter, appliedFilters, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, appliedFilters, page, pageSize]);

  useEffect(() => {
    getLeadFilterOptions()
      .then((options) => {
        setSourceOptions(options.sources || []);
        setStatusOptions(options.statuses?.length ? options.statuses : defaultStatusOptions);
      })
      .catch(() => {
        setSourceOptions([]);
        setStatusOptions(defaultStatusOptions);
      });
  }, []);

  const handleFilterSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return rows;

    return rows.filter((row) =>
      [
        row.project_name,
        row.developer,
        row.main_consultant,
        row.main_contractor,
        row.state,
        row.source,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword))
    );
  }, [rows, search]);

  const handleArchive = async (id) => {
    try {
      await archiveNews(id);
      await loadRows();
    } catch (err) {
      setError(err.message || "Failed to archive news.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreNews(id);
      await loadRows();
    } catch (err) {
      setError(err.message || "Failed to restore news.");
    }
  };

  const handleSyncMyBina = async () => {
    setSyncingMyBina(true);
    setSyncMessage("");
    setSyncError("");
    setError("");

    try {
      const response = await syncMyBinaProjects(3);
      if (response?.success === false) {
        throw new Error(response.message || "Failed to synchronize myBina projects.");
      }
      const result = response?.data || {};
      setSyncMessage(
        `myBina 3-month sync completed: ${result.created ?? 0} created, ${result.updated ?? 0} updated, ${result.skipped ?? 0} skipped.`
      );
      await loadRows();
    } catch (err) {
      setSyncError(err.message || "Failed to synchronize myBina projects.");
    } finally {
      setSyncingMyBina(false);
    }
  };

  return (
    <div className="news-page">
      <div className="news-page__topbar">
        <div>
          <h2>Construction News & Opportunities</h2>
          <p>Review imported projects before they continue to the external system.</p>
        </div>
        <button
          className="news-page__mybina-button"
          type="button"
          disabled={syncingMyBina}
          onClick={handleSyncMyBina}
        >
          {syncingMyBina ? "Syncing myBina..." : "Sync myBina (3m)"}
        </button>
      </div>

      {syncMessage && <div className="news-page__sync-message">{syncMessage}</div>}
      {syncError && (
        <div className="news-page__sync-message news-page__sync-message--error" role="alert">
          {syncError}
        </div>
      )}

      <form className="news-page__filters" onSubmit={handleFilterSubmit}>
        <input
          placeholder="Search project, developer, consultant..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={statusFilter} onChange={(event) => { setPage(1); setStatusFilter(event.target.value); }}>
          <option value="">All statuses</option>
          {statusOptions.map((status) => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
        <select value={filters.source} onChange={(event) => setFilters((current) => ({ ...current, source: event.target.value }))}>
          <option value="">All sources</option>
          {sourceOptions.map((source) => <option key={source} value={source}>{source}</option>)}
        </select>
        <input
          placeholder="Location / state"
          value={filters.location}
          onChange={(event) => setFilters((current) => ({ ...current, location: event.target.value }))}
        />
        <input
          type="number"
          min="0"
          max="100"
          placeholder="Min AI score"
          value={filters.ai_score_min}
          onChange={(event) => setFilters((current) => ({ ...current, ai_score_min: event.target.value }))}
        />
        <input
          type="number"
          min="0"
          max="100"
          placeholder="Max AI score"
          value={filters.ai_score_max}
          onChange={(event) => setFilters((current) => ({ ...current, ai_score_max: event.target.value }))}
        />
        <input
          type="number"
          min="0"
          placeholder="Min value (RM)"
          value={filters.project_value_min}
          onChange={(event) => setFilters((current) => ({ ...current, project_value_min: event.target.value }))}
        />
        <input
          type="number"
          min="0"
          placeholder="Max value (RM)"
          value={filters.project_value_max}
          onChange={(event) => setFilters((current) => ({ ...current, project_value_max: event.target.value }))}
        />
        <button type="submit" className="news-page__filter-button">Apply</button>
      </form>

      {error && <div className="news-page__table-wrapper">{error}</div>}

      <div className="news-page__table-wrapper">
        <table className="news-page__table">
          <thead>
            <tr>
              <th className="sticky-col">Actions</th>
              <th>Project / Opportunity</th>
              <th>AI Score</th>
              <th>Status</th>
              <th>Stage</th>
              <th>Project Value</th>
              <th>State</th>
              <th>Developer</th>
              <th>Issue</th>
              <th>Main Consultant</th>
              <th>Main Contractor</th>
              <th>Construction Start</th>
              <th>Lead Score</th>
              <th>Source</th>
              <th>Last Imported</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="15">{loading ? "Loading..." : "No records found."}</td>
              </tr>
            ) : (
              filteredRows.map((row, index) => (
                <React.Fragment key={row.id}>
                  <tr className={`record-row record-row--main record-row--${index % 2 === 0 ? "even" : "odd"}`}>
                    <td className="sticky-col actions-cell" rowSpan={2}>
                      <div className="action-buttons" aria-label={`Actions for ${row.project_name}`}>
                        {getExternalSourceUrl(row) && (
                          <a
                            className="icon-button icon-button--external"
                            href={getExternalSourceUrl(row)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open external source"
                            aria-label={`Open external source for ${row.project_name}`}
                          >
                            ↗
                          </a>
                        )}
                        {statusFilter === "archived" ? (
                          <button type="button" className="icon-button icon-button--restore" title="Restore" onClick={() => handleRestore(row.id)}>↩</button>
                        ) : (
                          <>
                            <button type="button" className="icon-button icon-button--archive" title="Archive" onClick={() => handleArchive(row.id)}>🗑</button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="project-cell">
                      <strong title={row.project_name}>{truncateText(row.project_name, 50)}</strong>
                      <span>{row.category || "-"}</span>
                    </td>
                    <td><span className={getScoreClass(Number(row.confidence_score || 0) * 100 || 0)}>{Math.round(Number(row.confidence_score || 0) * 100)}</span></td>
                    <td><span className={getLeadStatusClass(row.status)}>{getLeadStatusLabel(row.status)}</span></td>
                    <td>{row.stage || "-"}</td>
                    <td><strong>{formatProjectValue(row.project_value_amount)}</strong></td>
                    <td>{row.state || "-"}</td>
                    <td>{row.developer || "-"}</td>
                    <td>{row.issue || "-"}</td>
                    <td>{row.main_consultant || "-"}</td>
                    <td>{row.main_contractor || "-"}</td>
                    <td>{row.construction_start || "-"}</td>
                    <td>{row.lead_score ?? 0}</td>
                    <td>{row.source || "-"}</td>
                    <td>{row.last_imported_at || "-"}</td>
                  </tr>
                  <tr className={`record-row record-row--detail record-row--${index % 2 === 0 ? "even" : "odd"}`}>
                    <td colSpan="14">
                      <div className="detail-grid">
                        <div><label>Issue</label><strong>{row.issue || "-"}</strong></div>
                        <div><label>Consultant</label><strong>{row.main_consultant || "-"}</strong></div>
                        <div><label>Contractor</label><strong>{row.main_contractor || "-"}</strong></div>
                        <div><label>Construction Start</label><strong>{row.construction_start || "-"}</strong></div>
                        <div><label>Lead Score</label><strong>{row.lead_score ?? 0}</strong></div>
                        <div><label>Source</label><strong>{row.source || "-"}</strong></div>
                        <div><label>Last Imported</label><strong>{row.last_imported_at || "-"}</strong></div>
                      </div>
                      <ConsolidatedSources records={row.source_records || []} />
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
        <PaginationControls
          pagination={pagination}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
}
